import React from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import MenuClose from 'assets/icons/MenuClose.png'

interface TerminatePackAllocationDialogProps {
  faberToken: string
  handleTerminateAllocationDialogClose: () => void
  dialogOpen: boolean
  allocationId: string | null
}

function TerminateAllocationPackDialog({
  faberToken,
  allocationId,
  handleTerminateAllocationDialogClose,
  dialogOpen,
}: TerminatePackAllocationDialogProps) {
  const queryClient = useQueryClient()
  const { mutate: terminatePackAllocation, isLoading: isTerminatePackLoading } = useMutation(
    EsimAPI.terminatePackAllocation,
    {
      onSuccess: () => {
        toast.success('Pack termination successful')
        queryClient.refetchQueries({ queryKey: ['customer-allocation-packs'] });
      },
      onError: () => {
        toast.error('Error in terminating pack allocation')
      },
      onSettled: () => {
        handleTerminateAllocationDialogClose()
      },
    }
  )

  const handleTerminateAllocation = () => {
    if (allocationId) {
      terminatePackAllocation({ allocationId, faberToken })
    } else {
      toast.error('No allocation selected')
    }
  }

  return (
    <Dialog
      open={dialogOpen}
      maxWidth="sm"
      fullWidth={true}
      onClose={handleTerminateAllocationDialogClose}
      sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
    >
      <DialogTitle
        sx={{
          fontWeight: '700',
          fontSize: '20px',
          color: '#333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Terminate Pack Allocation
        <IconButton
          onClick={handleTerminateAllocationDialogClose}
          size="small"
          sx={{
            marginRight: '-8px',
            marginTop: '-8px',
          }}
        >
          <img
            style={{
              width: '20px',
              height: '20px',
            }}
            src={MenuClose}
            alt="Close"
          />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            marginTop: 2,
            marginBottom: 2,
            color: '#555',
          }}
        >
          Allocation ID: {allocationId}
          <br />
          Are you sure you want to terminate this pack? This action cannot be reversed.
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          padding: '16px',
          display: 'flex',
        }}
      >
        <LoadingButton variant='text' onClick={handleTerminateAllocationDialogClose}>
          Cancel
        </LoadingButton>

        <LoadingButton
          variant="contained"
          onClick={handleTerminateAllocation}
          loading={isTerminatePackLoading}
          loadingIndicator={<CircularProgress size={24} />}
          sx={{
            paddingX: 4,
            fontWeight: 'bold',
          }}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default TerminateAllocationPackDialog